import React, { useState } from 'react';
import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useLocation, useNavigate } from 'react-router';

import { useCurrentUser } from '../../auth/useCurrentUser';
import { useAppLogout } from '../../auth/useAppLogout';
import { paths } from '../../routes/paths';
import { colors } from '../../theme/tokens';

/**
 * Bottom user block in the sidebar. Shows avatar initials + name + email
 * and exposes a logout action via a kebab menu. The logout helper clears
 * tenant localStorage + react-query cache before calling Auth0.
 */
const UserBlock: React.FC = () => {
  const currentUser = useCurrentUser();
  const logout = useAppLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  // Admin entry — visible only to super-admins when the kill switch is
  // on. AdminLayout receives tenant context via location.state so it can
  // stash the return path for the "Return to tenant X" pill.
  const showAdminEntry = currentUser.isSuperAdmin && currentUser.adminViewEnabled;
  const tenantCodeFromUrl = React.useMemo(() => {
    const m = location.pathname.match(/^\/tenants\/([^/]+)/);
    return m ? m[1] : undefined;
  }, [location.pathname]);

  const handleEnterAdmin = () => {
    setAnchor(null);
    navigate(paths.admin.home(), {
      state: tenantCodeFromUrl
        ? { tenantCode: tenantCodeFromUrl, path: location.pathname + location.search }
        : undefined,
    });
  };

  return (
    <Box sx={{ flexShrink: 0, pb: 3 }}>
      <Divider sx={{ borderColor: colors.borderLight, my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: colors.brand,
            fontSize: 16,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {currentUser.initials}
        </Avatar>
        <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {currentUser.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: colors.textSecondary,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {currentUser.email}
          </Typography>
        </Box>
        <IconButton
          aria-label="Open account menu"
          aria-controls={open ? 'user-block-menu' : undefined}
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: colors.textSecondary }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      <Menu
        id="user-block-menu"
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {showAdminEntry && (
          <MenuItem onClick={handleEnterAdmin}>
            <AdminPanelSettingsOutlinedIcon
              sx={{ fontSize: 18, mr: 1.5, color: colors.adminAccent }}
            />
            <Typography sx={{ fontSize: 14 }}>Admin view</Typography>
          </MenuItem>
        )}
        {showAdminEntry && <Divider sx={{ my: 0.5 }} />}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
          }}
        >
          <LogoutIcon sx={{ fontSize: 18, mr: 1.5, color: colors.textSecondary }} />
          <Typography sx={{ fontSize: 14 }}>Sign out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserBlock;
